import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import {
  AssetPathService,
  PlatformInformationService
} from '../../../../projects/platform-manager/src/public_api';
import { routeLoaderMap } from '../routes/loader-map';
import { ActivatedRouteSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Meta } from '@angular/platform-browser';
import { ShopListInterface } from '../interfaces/shop-list-interface';
import { ProductCategory } from '../../pages/product-selection/services/entities';
import { LegacyProductDto } from '../../pages/product-selection/generated';
import {
  TrustPilotDataInterface,
  TrustPilotReviewsInterface
} from '../components/trustpilot/trustpilot-interface';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class StructuredDataService {
  private renderer2: Renderer2;
  private jsonLdGraph: object[] = [];

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private platformService: PlatformInformationService,
    private translateService: TranslateService,
    private meta: Meta,
    private assetPathService: AssetPathService,
    private httpClient: HttpClient
  ) {
    this.renderer2 = rendererFactory.createRenderer(null, null);
  }

  async setAlternateLangLinks(
    pathFromRoot: ActivatedRouteSnapshot[],
    params?: { [lang: string]: { key: string; value: string } }
  ) {
    const segments: string[] = [];
    const pams = [];
    const queryParams = {};
    // go through every "step" of the router and find the localizeRoute data to translate for
    for (const snapshot of pathFromRoot) {
      Object.keys(snapshot.queryParams).forEach(key => {
        queryParams[key] = snapshot.queryParams[key];
      });
      if (snapshot.data?.localizeRouter?.path) {
        const path = snapshot.data.localizeRouter.path;
        pams.push(...pams);
        const translated = path.split('/').map(part => {
          return part;
        });

        segments.push(...translated);
      }
    }
    // step 2, translate if translateable
    const translations = [];
    for (const lang of this.platformService.getAvailableLanguagesForPlatform()) {
      const langRoutes = (await routeLoaderMap[lang]()) as object;
      let route = lang;
      segments.forEach(segment => {
        route +=
          '/' + (this.traverseRouteTranslation(segment.split('.'), 0, langRoutes['ROUTES'], pams) ?? segment);
      });
      // set the params if available
      if (params && params[lang]) {
        Object.keys(params[lang]).forEach(key => {
          route = route.replace(key, params[lang][key]);
        });
      }
      let queryString = '';
      Object.keys(queryParams).forEach(key => {
        queryString += `${key}=${encodeURI(queryParams[key])}&`;
      });
      queryString.substring(0, queryString.length - 1);

      if (queryString.length) {
        translations.push({ lang: lang, href: `/${route}?${queryString}` });
      } else {
        translations.push({ lang: lang, href: '/' + route });
      }
    }

    // step 3, set/update the hreflang alternate links in the document
    const origin = this.platformService.getOrigin();
    translations.forEach(alternate => {
      const link = this.renderer2.createElement('link');
      link.rel = 'alternate';
      link.hreflang = alternate.lang;
      link.href = Location.joinWithSlash(origin, alternate.href);
      link.id = 'alternate_' + alternate.lang;
      const replaceMe = this.document.getElementById(link.id);
      if (replaceMe) {
        replaceMe.remove();
      }
      this.renderer2.appendChild(this.document.head, link);
    });
  }

  private traverseRouteTranslation(tokens, index = 0, routeTranslation, params) {
    if (routeTranslation[tokens[index]] && typeof routeTranslation[tokens[index]] === 'object') {
      return this.traverseRouteTranslation(tokens, index + 1, routeTranslation[tokens[index]], params);
    } else if (
      routeTranslation[tokens[index]] &&
      typeof routeTranslation[tokens[index]] === 'string' &&
      tokens.length - 1 === index
    ) {
      return routeTranslation[tokens[index]];
    } else {
      return null;
    }
  }

  clearMetaTags() {
    const elements = Array.from(this.document.getElementsByClassName('metaTag'));
    // Iterate over the array and remove each element from its parent
    for (let i = 0; i < elements.length; i++) {
      elements[i].parentNode.removeChild(elements[i]);
    }
  }

  setMetaTags(tags: { name: string; content: string }[], params?: object) {
    this.translateService
      .get(
        tags.map(t => t.content),
        params
      )
      .subscribe(translations => {
        tags.forEach(tag => {
          this.meta.updateTag({
            name: tag.name,
            content: translations[tag.content],
            class: 'metaTag'
          });
        });
      });
    this.setOgUpdatedTime();
  }

  private setOgUpdatedTime(): void {
    const now = new Date();
    const currentDay = now.getDate();

    // If before the 28th, go to previous month
    const month = currentDay < 28 ? now.getMonth() - 1 : now.getMonth();
    const year = month < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const adjustedMonth = (month + 12) % 12;

    const updated = new Date(year, adjustedMonth, 28, 12, 0, 0);
    this.meta.updateTag({
      name: 'og:updated_time',
      content: updated.toISOString(),
      class: 'metaTag'
    });
  }

  /**
   * Injects or replaces a JSON-LD script in the <head>.
   * @param json JSON object to inject.
   * @param id Optional unique ID to manage this script (default 'json-ld').
   */
  setJsonLd(json: object, id = 'default-json-ld'): void {
    // Remove existing script with the same ID
    const oldScript = this.document.getElementById(id);
    if (oldScript) {
      oldScript.remove();
    }

    // Create new script
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(json, null, 2);
    this.document.head.appendChild(script);
  }

  setBreadcrumbList(list: { name: string; link: string }[]) {
    const host = this.platformService.getOrigin();
    this.upsertToJsonLdGraph(
      {
        '@type': 'BreadcrumbList',
        itemListElement: list.map((el, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            name: el.name,
            item: host + el.link
          };
        })
      },
      'breadcrumb'
    );
  }

  setSportingGoodsStoreItemList(list: {
    name: string;
    elements: { url: string; shop: ShopListInterface }[];
  }) {
    this.upsertToJsonLdGraph(
      {
        '@type': 'ItemList',
        name: list.name,
        numberOfItems: list.elements.length,
        itemListElement: list.elements.map((el, i) => {
          return {
            '@type': 'ListItem',
            position: i + 1,
            url: el.url
          };
        })
      },
      'itemlist'
    );
    list.elements.forEach((el, index) => {
      this.upsertToJsonLdGraph(this.generateSportingGoodsStore(el.shop), 'listEl_' + index);
    });
  }

  setShopJsonLd(shop: ShopListInterface): void {
    this.upsertToJsonLdGraph(this.generateSportingGoodsStore(shop), `shop-${shop.id}`);
  }

  private generateSportingGoodsStore(shop: ShopListInterface): object {
    const address = shop.address.split(', ');
    const jsonLd = {
      '@type': 'SportingGoodsStore',
      name: shop.name,
      image: [shop.imagePathBig, ...shop.additionalImagePaths.map(p => p.replace('%%SIZE%%', 'big'))].map(
        img => this.assetPathService.getAssetsPath(img, false, true, '')
      ),
      address: {
        '@type': 'PostalAddress',
        streetAddress: address.slice(0, address.length - 1).join(', '),
        addressLocality: shop.townName,
        addressCountry: shop.countryCode,
        postalCode: address[address.length - 1].split(' ')[0]
      },
      priceRange: '$ - $$$', // this field is an enigma and it looks like everything goes: https://github.com/schemaorg/schemaorg/issues/1307
      openingHours: shop.businessHours.map(
        bh => `${bh.range.map(day => day.substring(0, 2)).join('-')} ${bh.hours}`
      )
    };

    if (shop.phone) jsonLd['telephone'] = shop.phone;
    if (shop.email) jsonLd['email'] = shop.email;
    if (jsonLd.openingHours.length === 0) {
      delete jsonLd.openingHours;
    }

    return jsonLd;
  }

  setProductsJsonLd(
    shop: ShopListInterface,
    products: (LegacyProductDto & { hasStopProduct: boolean })[]
  ): void {
    const lang = this.translateService.currentLang || 'en';
    const productsByCategory = this.groupProductsByCategory(products);

    const productListJsonLd = {
      '@type': 'ItemList',
      name: `${shop.name} - Product Selection`,
      itemListElement: products.map((product, index) => this.mapProductToJsonLd(product, index + 1, lang))
    };

    this.upsertToJsonLdGraph(productListJsonLd, 'product-list');

    Object.keys(productsByCategory).forEach(categoryKey => {
      const categoryProducts = productsByCategory[+categoryKey];
      if (categoryProducts?.length > 0) {
        const productGroupJsonLd = {
          '@type': 'ProductGroup',
          name: `${ProductCategory[+categoryKey]} rental`,
          description: `Various ${ProductCategory[+categoryKey]} models available for rent`,
          productGroupID: ProductCategory[+categoryKey],
          hasVariant: categoryProducts.map((product, index) =>
            this.mapProductToJsonLd(product, index + 1, lang)
          )
        };

        this.upsertToJsonLdGraph(productGroupJsonLd, `product-group-${categoryKey}`);
      }
    });
  }

  /**
   * Injects Organization-level Trustpilot data with aggregate rating.
   */
  setTrustpilotCompanyJsonLd(companyData: TrustPilotDataInterface): void {
    const organization = {
      '@type': 'Organization',
      name: companyData.displayName,
      url: companyData.websiteUrl,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: companyData.score.trustScore.toFixed(1),
        reviewCount: companyData.numberOfReviews.total
      }
    };

    this.upsertToJsonLdGraph(organization, 'trustpilot-company');
  }

  /**
   * Injects Trustpilot reviews as individual Review items.
   */
  setTrustpilotReviewsJsonLd(reviews: TrustPilotReviewsInterface['reviews']): void {
    reviews.slice(0, 6).forEach((review, index) => {
      const reviewJson = {
        '@type': 'Review',
        itemReviewed: {
          '@type': 'Organization',
          name: review.businessUnit.displayName,
          url: 'https://alpy.com'
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.stars,
          bestRating: '5'
        },
        author: {
          '@type': 'Person',
          name: review.consumer.displayName
        },
        reviewBody: review.text,
        datePublished: new Date(review.createdAt).toISOString().split('T')[0]
      };

      this.upsertToJsonLdGraph(reviewJson, `trustpilot-review-${index}`);
    });
  }

  /**
   * Converts a single product to JSON-LD format.
   */
  private mapProductToJsonLd(
    product: LegacyProductDto & { hasStopProduct: boolean },
    position: number,
    lang: string
  ): object {
    return {
      '@type': 'Product',
      position,
      name: product.name[lang] || product.defaultModelName,
      image: this.assetPathService.getAssetsPath(product.imagePath, false, true, ''),
      description: product.descriptionForAcceptedLanguages?.[lang] || '',
      category: ProductCategory[product.productCategoryId] || 'Equipment',
      offers: {
        '@type': 'Offer',
        priceCurrency: product.priceCurrencyCode || 'EUR',
        price: Object.values(product.price)[0] || 0,
        availability: product.hasStopProduct ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock'
      }
    };
  }

  /**
   * Groups products by category.
   */
  private groupProductsByCategory(products: (LegacyProductDto & { hasStopProduct: boolean })[]): {
    [key: number]: (LegacyProductDto & { hasStopProduct: boolean })[];
  } {
    return products.reduce(
      (acc, product) => {
        if (!acc[product.productCategoryId]) {
          acc[product.productCategoryId] = [];
        }
        acc[product.productCategoryId].push(product);
        return acc;
      },
      {} as { [key: number]: (LegacyProductDto & { hasStopProduct: boolean })[] }
    );
  }

  /**
   * Adds or replaces a structured data object in the internal @graph
   * and updates the single <script type="application/ld+json"> in <head>.
   */
  private upsertToJsonLdGraph(entry: object, key: string): void {
    const existingIndex = this.jsonLdGraph.findIndex(item => item['@_key'] === key);

    const wrapped = { ...entry, ['@_key']: key };

    if (existingIndex !== -1) {
      this.jsonLdGraph[existingIndex] = wrapped;
    } else {
      this.jsonLdGraph.push(wrapped);
    }

    this.renderJsonLdGraph();
  }

  /**
   * Re-renders the full JSON-LD <script> with @graph in the <head>.
   */
  private renderJsonLdGraph(): void {
    const graph = this.jsonLdGraph.map(item => {
      const clone = { ...item };
      delete clone['@_key'];
      return clone;
    });

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': graph
    };

    const scriptId = 'structured-data-graph';

    const oldScript = this.document.getElementById(scriptId);
    if (oldScript) {
      oldScript.remove();
    }

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.text = JSON.stringify(jsonLd, null, 2);
    this.document.head.appendChild(script);
  }

  setCompanyJsonLd(): void {
    if (!this.platformService.getCurrentPartner()) {
      const path = this.assetPathService.getAssetsPath(
        `${this.platformService.getCurrentPlatformShortcode().toLowerCase()}.json`,
        true,
        false,
        '/assets/company-data'
      );
      this.httpClient.get<object>(path).subscribe(companyData => {
        this.upsertToJsonLdGraph(companyData, 'company-data');
        this.addWebsiteData();
      });
    }
  }

  /**
   * Adds a WebSite node for Google's Site Name system.
   * Only runs when current platform is "ar" and there is NO partner.
   * Produces a localized "Alpy.com <Ski rental>" site name and alternates.
   */
  addWebsiteData(): void {
    const origin = this.platformService.getOrigin().replace(/\/+$/, ''); // no trailing slash
    const brand = this.deriveBrandFromOrigin(this.platformService.getOrigin());

    const localizedName = `${brand} ${this.translateService.instant('CORE.skiHire')}`;

    const websiteJsonLd = {
      '@type': 'WebSite',
      '@id': `${origin}/#website`,
      url: `${origin}/`,
      name: localizedName,
      alternateName: `${brand} Ski Rental`,
      inLanguage: this.translateService.currentLang
    };

    this.upsertToJsonLdGraph(websiteJsonLd, 'website');
  }

  private deriveBrandFromOrigin(origin: string): string {
    try {
      const hostname = new URL(origin).hostname || '';
      const core = hostname.replace(/^www\./i, '');
      if (!core) return 'Alpy.com';
      return core.charAt(0).toUpperCase() + core.slice(1);
    } catch {
      const core = (origin || '')
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .split('/')[0];
      if (!core) return 'Alpy.com';
      return core.charAt(0).toUpperCase() + core.slice(1);
    }
  }
}
